import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, ArrowRight, X } from "lucide-react";

const HIDDEN_KEY = "akm_onboarding_banner_dismissed";

/**
 * Shown on the dashboard when the current hotel_admin still hasn't finished
 * the setup wizard. Dismissable per-session via localStorage; auto-hides when
 * wizard is completed.
 */
export default function OnboardingBanner() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [dismissed, setDismissed] = useState(() =>
        typeof window !== "undefined" && localStorage.getItem(HIDDEN_KEY) === "1"
    );

    useEffect(() => {
        // Only hotel admins get the banner; other roles skip the API call.
        if (!user || user.role !== "hotel_admin") return;
        let cancelled = false;
        api.get("/onboarding/status")
            .then(({ data }) => { if (!cancelled) setStatus(data); })
            .catch(() => { /* silently ignore */ });
        return () => { cancelled = true; };
    }, [user]);

    if (!status?.needs_wizard || dismissed) return null;

    const dismiss = () => {
        localStorage.setItem(HIDDEN_KEY, "1");
        setDismissed(true);
    };

    return (
        <div
            className="relative mb-4 overflow-hidden rounded-lg border border-[#ff7b02]/40 bg-gradient-to-r from-[#ff7b02]/10 via-[#ff7b02]/5 to-transparent p-4 dark:from-[#ff7b02]/20 dark:via-[#ff7b02]/10"
            data-testid="onboarding-banner"
        >
            <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-[#ff7b02] text-white shadow">
                    <Sparkles size={16} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground">Përfundoni konfigurimin e hotelit</div>
                    <div className="mt-0.5 text-[12.5px] text-muted-foreground">
                        Ju kanë mbetur disa hapa për të aktivizuar Booking Engine dhe filluar rezervimet online.
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Link
                            to={`/wizard/${status.draft_id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-[#ff7b02] px-2.5 py-1.5 text-[11.5px] font-semibold text-white transition hover:bg-[#ff7b02]/90"
                            data-testid="onboarding-banner-resume"
                        >
                            Vazhdo wizard-in <ArrowRight size={12} />
                        </Link>
                        <button
                            onClick={dismiss}
                            className="text-[11.5px] text-muted-foreground hover:text-foreground"
                            data-testid="onboarding-banner-dismiss"
                        >
                            Më kujto më vonë
                        </button>
                    </div>
                </div>
                <button
                    onClick={dismiss}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Mbyll"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
}
